import { useState } from 'react'
import type { ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import type { SolutionCard } from '@/shared/lib/apiTypes'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { CallBellIcon } from '@/shared/ui/icons/CallBellIcon'
import { UserIcon } from '@/shared/ui/icons/UserIcon'
import { LocationPinIcon } from '@/shared/ui/icons/LocationPinIcon'
import { MoneyIcon } from '@/shared/ui/icons/MoneyIcon'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { ChevronDownIcon } from '@/shared/ui/icons/ChevronDownIcon'
import { ChevronUpIcon } from '@/shared/ui/icons/ChevronUpIcon'
import { CERT_LABEL, useTargetCodes } from '@/shared/lib/codes'
import { useSolutionCardLabels } from '@/shared/lib/cardLabels'
import { GrayChip } from '@/shared/ui/GrayChip'

const CATEGORY_CHIP_MAX = 4

function PanelDivider() {
  return (
    <span className="w-full py-[2px]" aria-hidden="true">
      <span className="block h-px w-full rounded-[999px] bg-white-300" />
    </span>
  )
}

function RowLabel({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <span className="flex min-w-px flex-1 items-center gap-1 text-body-sm font-medium text-black-100">
      {icon}
      <span>{label}</span>
    </span>
  )
}

function ValueRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  if (value.length === 0) return null

  return (
    <div className="flex h-[34px] w-full items-center gap-2 px-1">
      <RowLabel icon={icon} label={label} />
      <span className="shrink-0 text-body-md font-semibold text-black-300">{value}</span>
    </div>
  )
}

function FoldRow({
  icon,
  label,
  summary,
  values,
}: {
  icon: ReactNode
  label: string
  summary: string
  values: string[]
}) {
  const [open, setOpen] = useState(false)

  if (values.length === 0) return null

  return (
    <section className="flex w-full flex-col gap-2.5">
      <button
        type="button"
        className="ui-pressable flex w-full items-center gap-2 border-0 bg-transparent px-1 py-0"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <RowLabel icon={icon} label={label} />
        <span className="flex shrink-0 items-center gap-1.5 py-1.5 text-body-md font-semibold text-black-300">
          {summary}
          <span className="text-black-100">
            {open ? <ChevronUpIcon size={22} /> : <ChevronDownIcon size={22} />}
          </span>
        </span>
      </button>
      {open && (
        <div className="flex flex-wrap items-start gap-2.5">
          {values.map((text) => (
            <span
              key={text}
              className="inline-flex max-w-[126px] min-w-16 shrink-0 items-center justify-center rounded-lg bg-violet-100 px-3 py-[3px] text-body-sm font-medium whitespace-nowrap text-violet-700"
            >
              {text}
            </span>
          ))}
        </div>
      )}
    </section>
  )
}

export function SolutionDetailMobile({ query }: { query: UseQueryResult<SolutionCard> }) {
  const labels = useSolutionCardLabels()
  const targetCodes = useTargetCodes()
  const targetTotal = targetCodes.data?.length ?? 0
  const c = query.data
  const resourceValues = (c?.resourceCodes ?? []).map(labels.resourceLabel)
  const targetValues = (c?.targetCodes ?? []).map(labels.targetLabel)
  const regionValues =
    c == null
      ? []
      : c.serviceDongCodes != null && c.serviceDongCodes.length > 0
        ? c.serviceDongCodes.map(labels.dongLabel)
        : c.serviceAreaCodes.map(labels.serviceAreaLabel)

  return (
    <DefaultLayout mobileTitle="해결카드 상세" className="pc:hidden flex min-h-[100dvh] flex-col">
      <QueryState isPending={query.isPending || !labels.isReady} error={query.error}>
        {c ? (
          <>
            <article className="layout-body flex flex-col">
              <div className="flex flex-col gap-9 p-4">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-[99px] border-[1.563px] border-[rgb(0_0_0/5%)] bg-violet-100 text-black-100 [&>svg]:opacity-50"
                    >
                      <CityIcon size={20} />
                    </span>
                    <span className="flex min-w-0 flex-col justify-center gap-[3px]">
                      <span className="truncate text-body-md-reading font-medium text-black-500">
                        {c.org.name}
                      </span>
                      {c.org.certType !== null && CERT_LABEL[c.org.certType] !== undefined && (
                        <span className="truncate text-body-sm text-black-100">
                          {CERT_LABEL[c.org.certType]}
                        </span>
                      )}
                    </span>
                  </div>

                  <h1 className="m-0 text-headline-xs leading-[1.7] font-medium tracking-[-0.1px] text-black-800">
                    {c.title}
                  </h1>
                  {c.summary.length > 0 && (
                    <p className="m-0 text-body-md-reading text-black-800">{c.summary}</p>
                  )}
                </div>

                {c.categoryCodes.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2.5">
                    {c.categoryCodes.slice(0, CATEGORY_CHIP_MAX).map((code) => (
                      <GrayChip key={code} paddingClass="px-4">
                        {labels.categoryLabel(code)}
                      </GrayChip>
                    ))}
                    {c.categoryCodes.length > CATEGORY_CHIP_MAX && (
                      <span className="inline-flex shrink-0 items-center justify-center rounded-lg border border-white-500 bg-white-50 px-4 py-[3px] text-body-sm font-medium whitespace-nowrap text-black-400">
                        +{c.categoryCodes.length - CATEGORY_CHIP_MAX}
                      </span>
                    )}
                  </div>
                )}

                {c.accessCondition !== null && c.accessCondition.length > 0 && (
                  <p className="m-0 flex items-center gap-1 text-body-sm font-normal text-violet-600">
                    <InfoIcon size={20} />
                    <span>{c.accessCondition}</span>
                  </p>
                )}
              </div>

              <span className="h-2 w-full bg-violet-50" aria-hidden="true" />

              <div className="flex flex-col gap-4 py-4">
                <h2 className="m-0 px-4 text-body-sm font-semibold text-black-800">지원 정보</h2>
                <div className="flex flex-col gap-2.5 px-4">
                  <FoldRow
                    icon={<CallBellIcon size={22} />}
                    label="제공 서비스"
                    summary={`${resourceValues.length}개`}
                    values={resourceValues}
                  />
                  <PanelDivider />
                  <FoldRow
                    icon={<UserIcon size={22} />}
                    label="해결 대상"
                    summary={
                      targetTotal > 0 && targetValues.length === targetTotal
                        ? '전체'
                        : `${targetValues.length}개`
                    }
                    values={targetValues}
                  />
                  <PanelDivider />
                  <ValueRow
                    icon={<LocationPinIcon size={22} />}
                    label="지역"
                    value={regionValues.join(' · ')}
                  />
                  <PanelDivider />
                  <ValueRow
                    icon={<MoneyIcon size={22} />}
                    label="비용"
                    value={c.costType.length > 0 ? labels.costTypeLabel(c.costType) : ''}
                  />
                </div>
              </div>
            </article>

            <div className="sticky bottom-0 mt-auto w-full bg-white-50 px-4 py-2">
              <Link
                className="ui-pressable flex h-12 items-center justify-center rounded-[14px] border border-white-400 bg-white-50 px-[22px] text-body-lg font-normal text-black-600 no-underline"
                to={`/orgs/${c.org.id}`}
              >
                기업 자세히 보기
              </Link>
            </div>
          </>
        ) : null}
      </QueryState>
    </DefaultLayout>
  )
}
